import React, { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { FiCopy, FiThumbsDown, FiThumbsUp, FiVolume2 } from 'react-icons/fi'
import { clearMessageFeedback, fetchAssistantAttachment, saveMessageFeedback } from './assistantApi'
import { readAloud, readingSupported } from './assistantSpeech'

const REASONS = [
  { id: 'incorrect', message: { id: 'assistant_reason_incorrect', defaultMessage: 'Incorrect' } },
  { id: 'incomplete', message: { id: 'assistant_reason_incomplete', defaultMessage: 'Incomplete' } },
  { id: 'unrelated', message: { id: 'assistant_reason_unrelated', defaultMessage: 'Unrelated' } },
  { id: 'unclear', message: { id: 'assistant_reason_unclear', defaultMessage: 'Hard to follow' } },
]

const ERRORS = {
  usage_limit: { id: 'assistant_usage_limit', defaultMessage: 'The daily assistant limit has been reached.' },
  timeout: { id: 'assistant_timeout', defaultMessage: 'The assistant did not answer in time.' },
  invalid_json: { id: 'assistant_invalid', defaultMessage: 'The assistant response could not be read.' },
  ai_unavailable: { id: 'assistant_unavailable', defaultMessage: 'The assistant is not available right now.' },
  failed: { id: 'assistant_failed', defaultMessage: 'The assistant could not answer.' },
}

export function citationTarget(citation, available) {
  if (!citation?.feature_id) return null
  return (available || []).find((feature) => feature.id === citation.feature_id) || null
}

export function labelLocale(userText, uiLocale) {
  const ui = String(uiLocale || '')
  if (ui.toLowerCase().startsWith('es')) return 'es'
  const text = String(userText || '')
  const folded = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  if (/[áéíóúñ]/i.test(text) || /\b(como|crear|creo|factura|donde|quiero|hacer|cotizacion|aprobar|procedimiento|pago)\b/.test(folded)) {
    return 'es'
  }
  return 'en'
}

function openWord(locale) {
  return locale === 'es' ? 'Abrir' : 'Open'
}

function bundleText(bundle, locale) {
  if (!bundle) return ''
  return locale === 'es' ? bundle.es : bundle.en
}

function clock(value, speak) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString(speak === 'es' ? 'es' : 'en', { hour: 'numeric', minute: '2-digit' })
}

export function uniqueMessages(rows) {
  const seen = new Set()
  const out = []
  ;(rows || []).forEach((row) => {
    if (!row) return
    const key = `${row.role || 'assistant'}:${row.id}`
    if (seen.has(key)) return
    const prev = out[out.length - 1]
    if (prev && prev.role === row.role && prev.content && prev.content === row.content) return
    seen.add(key)
    out.push(row)
  })
  return out
}

function AttachmentView({ file }) {
  const [url, setUrl] = useState(file.url || '')
  useEffect(() => {
    if (file.url || !file.id) return undefined
    let cancelled = false
    let created = ''
    fetchAssistantAttachment(file.id).then((blob) => {
      if (cancelled) return
      created = URL.createObjectURL(blob)
      setUrl(created)
    }).catch(() => {})
    return () => {
      cancelled = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [file])
  if (!url) return null
  return <img src={url} alt="" />
}

function viewOf(feedback) {
  if (!feedback || feedback.rating === 'positive') {
    return { mark: feedback && feedback.rating === 'positive' ? 'up' : '', reason: '' }
  }
  if (feedback.rating === 'negative') return { mark: 'down', reason: feedback.reason || '' }
  return { mark: '', reason: '' }
}

async function writeClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', '')
  area.style.position = 'fixed'
  area.style.left = '-9999px'
  document.body.appendChild(area)
  area.select()
  const ok = document.execCommand('copy')
  document.body.removeChild(area)
  if (!ok) throw new Error('copy')
}

function Turn({ message, available, onOpen, speak }) {
  const { formatMessage: trans } = useIntl()
  const initial = viewOf(message && message.feedback)
  const [mark, setMark] = useState(initial.mark)
  const [reason, setReason] = useState(initial.reason)
  const [note, setNote] = useState('')
  const [copyNote, setCopyNote] = useState('')
  const [speaking, setSpeaking] = useState(false)
  const voice = useRef(null)
  const saving = useRef(0)
  const desired = useRef('')
  const queue = useRef(Promise.resolve())
  const confirmed = useRef(initial)
  const textOf = (bundle) => bundleText(bundle, speak)

  useEffect(() => {
    if (saving.current > 0) return
    const next = viewOf(message && message.feedback)
    confirmed.current = next
    setMark(next.mark)
    setReason(next.reason)
    desired.current = ''
  }, [message])

  if (!message) return null
  const when = clock(message.created_at, speak)
  if (message.role === 'user') {
    const files = Array.isArray(message.attachments) ? message.attachments : []
    return (
      <div className="feature-search__bubble feature-search__bubble--user">
        {files.length > 0 ? (
          <div className="feature-search__sent-images">
            {files.map((file) => <AttachmentView key={file.id || file.url} file={file} />)}
            <p>{trans({ id: 'assistant_image_unread', defaultMessage: 'The image was saved. This answer uses the written question only.' })}</p>
          </div>
        ) : null}
        <p className="feature-search__user">{message.content}</p>
        {when ? <time className="feature-search__time">{when}</time> : null}
      </div>
    )
  }
  if (message.status === 'queued' || message.status === 'processing') return null
  if (message.status === 'failed') {
    const copy = ERRORS[message.error_code] || ERRORS.failed
    return <p className="feature-search__alert" role="alert">{trans(copy)}</p>
  }
  const citations = Array.isArray(message.citations) ? message.citations : []
  const rate = (nextRating, nextReason) => {
    const messageId = Number(message.id)
    if (!Number.isInteger(messageId) || messageId < 1) return
    const key = `${nextRating}:${nextReason || ''}`
    if (desired.current === key) return
    if (saving.current > 0 && !nextRating) return
    desired.current = key
    const shown = {
      mark: nextRating === 'positive' ? 'up' : nextRating === 'negative' ? 'down' : '',
      reason: nextRating === 'negative' ? (nextReason || '') : '',
    }
    setNote('')
    setMark(shown.mark)
    setReason(shown.reason)
    saving.current += 1
    queue.current = queue.current.then(async () => {
      try {
        if (!nextRating) {
          await clearMessageFeedback(messageId)
        } else {
          await saveMessageFeedback(messageId, nextRating, nextRating === 'negative' ? (nextReason || null) : null)
        }
        confirmed.current = shown
        if (desired.current === key) setNote('saved')
      } catch (error) {
        if (desired.current === key) {
          setMark(confirmed.current.mark)
          setReason(confirmed.current.reason)
          desired.current = ''
          setNote('error')
        }
      } finally {
        saving.current -= 1
      }
    })
  }
  const listen = () => {
    if (speaking && voice.current) {
      voice.current.stop()
      voice.current = null
      setSpeaking(false)
      return
    }
    const handle = readAloud(message.content, speak, () => setSpeaking(false))
    if (!handle) return
    voice.current = handle
    setSpeaking(true)
  }
  const copyAnswer = async () => {
    const text = String(message.content || '')
    if (!text) return
    try {
      await writeClipboard(text)
      setCopyNote('copied')
    } catch (error) {
      setCopyNote('error')
    }
  }
  return (
    <div className="feature-search__bubble feature-search__bubble--assistant">
      <div className="feature-search__turn">
        <p>{message.content}</p>
        {citations.map((citation) => {
          const feature = citationTarget(citation, available)
          if (!feature) return null
          return (
            <div className="feature-search__cite" key={citation.id}>
              <span className="feature-search__source">
                {trans({ id: 'assistant_source', defaultMessage: 'Source' })}
                {' · '}
                {textOf(feature.label)}
              </span>
              <button type="button" className="feature-search__open" onClick={() => onOpen(feature)}>
                {openWord(speak)} {textOf(feature.label)}
              </button>
            </div>
          )
        })}
        <div className="feature-search__feedback">
          <button type="button" className={mark === 'up' ? 'is-on' : ''} aria-pressed={mark === 'up'} aria-label={trans({ id: 'assistant_helpful', defaultMessage: 'Helpful' })} onClick={() => rate(mark === 'up' ? '' : 'positive', null)}>
            <FiThumbsUp aria-hidden="true" />
          </button>
          <button type="button" className={mark === 'down' ? 'is-on' : ''} aria-pressed={mark === 'down'} aria-label={trans({ id: 'assistant_not_helpful', defaultMessage: 'Not helpful' })} onClick={() => rate(mark === 'down' ? '' : 'negative', reason)}>
            <FiThumbsDown aria-hidden="true" />
          </button>
          <button type="button" aria-label={trans({ id: 'assistant_copy', defaultMessage: 'Copy' })} onClick={copyAnswer}>
            <FiCopy aria-hidden="true" />
          </button>
          {readingSupported() ? (
            <button type="button" className={speaking ? 'is-on' : ''} aria-pressed={speaking} aria-label={trans({ id: speaking ? 'assistant_stop_reading' : 'assistant_listen', defaultMessage: speaking ? 'Stop' : 'Listen' })} onClick={listen}>
              <FiVolume2 aria-hidden="true" />
            </button>
          ) : null}
          {when ? <time className="feature-search__time">{when}</time> : null}
        </div>
        {mark === 'down' ? (
          <div className="feature-search__reasons" role="group" aria-label={trans({ id: 'assistant_feedback_reason', defaultMessage: "Why wasn't this helpful?" })}>
            {REASONS.map((item) => (
              <button type="button" key={item.id} className={reason === item.id ? 'is-on' : ''} aria-pressed={reason === item.id} onClick={() => rate('negative', item.id)}>
                {trans(item.message)}
              </button>
            ))}
          </div>
        ) : null}
        {note === 'saved' ? <p className="feature-search__feedback-note" role="status">{trans({ id: 'assistant_feedback_saved', defaultMessage: 'Saved' })}</p> : null}
        {note === 'error' ? <p className="feature-search__feedback-note is-error" role="alert">{trans({ id: 'assistant_feedback_error', defaultMessage: 'The rating could not be saved.' })}</p> : null}
        {copyNote === 'copied' ? <p className="feature-search__feedback-note" role="status">{trans({ id: 'assistant_copied', defaultMessage: 'Copied' })}</p> : null}
        {copyNote === 'error' ? <p className="feature-search__feedback-note is-error" role="alert">{trans({ id: 'assistant_copy_failed', defaultMessage: 'Could not copy.' })}</p> : null}
      </div>
    </div>
  )
}

export default function AssistantThread({ pending, error, message, turns, available, onOpen }) {
  const { formatMessage: trans, locale } = useIntl()
  const history = uniqueMessages(Array.isArray(turns) ? turns : [])
  const visible = uniqueMessages(message ? history.concat([message]) : history)
  if (!pending && !error && visible.length === 0) return null

  return (
    <div className="feature-search__thread">
      {visible.map((row, index) => {
        let asked = ''
        for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
          if (visible[cursor] && visible[cursor].role === 'user') {
            asked = visible[cursor].content
            break
          }
        }
        return (
          <Turn
            key={`${row.role || 'assistant'}-${row.id}`}
            message={row}
            available={available}
            onOpen={onOpen}
            speak={labelLocale(asked, locale)}
          />
        )
      })}
      {pending ? (
        <p className="feature-search__pending" role="status">
          <span className="feature-search__dots" aria-hidden="true"><i /><i /><i /></span>
          {trans({ id: 'assistant_pending', defaultMessage: 'Preparing an answer…' })}
        </p>
      ) : null}
      {error ? <p className="feature-search__alert" role="alert">{trans(ERRORS[error] || ERRORS.failed)}</p> : null}
    </div>
  )
}
